import Image from 'next/image'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { ArrowRight, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ChaseLightButton } from '@/components/ui/chase-light-button'
import { gradientBtn } from '@/lib/ui-classes'
import { useUserStore } from '@/store/user'
import { HeroGlowBackground } from '@/components/home/HeroGlowBackground'

interface HeroSectionProps {
  onDiscoverWorkflow: () => void
}

export function HeroSection({ onDiscoverWorkflow }: HeroSectionProps) {
  const isAuthenticated = useUserStore(state => state.isAuthenticated)

  return (
    <section className="relative flex min-h-screen flex-col overflow-hidden">
      <HeroGlowBackground />

      <div className="relative z-10 flex flex-1 items-start justify-center px-4 pt-32 text-center sm:px-6 sm:pt-40">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mx-auto max-w-5xl space-y-10"
        >
          <div className="mx-auto w-full max-w-4xl px-2 sm:px-0">
            {/* <h1 className="sr-only">让 AI 创作者获取每一份应有的收益</h1> */}
            <Image
              src="/images/tips_text_image-a.png"
              alt="让 AI 创作者获取每一份应有的收益"
              width={1200}
              height={400}
              priority
              className="mx-auto h-auto w-full max-w-3xl object-contain"
            />
          </div>

          <div className="mx-auto flex max-w-xl flex-col items-center gap-4 px-2">
            <div className="flex w-full items-center gap-4">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent to-[#07F4F2]/45" aria-hidden />
              <span className="shrink-0 text-xs tracking-[0.28em] text-zinc-500">AI 漫剧工作台</span>
              <span className="h-px flex-1 bg-gradient-to-l from-transparent to-[#C1FAB8]/45" aria-hidden />
            </div>
            <p className="text-base leading-relaxed text-zinc-400 sm:text-lg">
              从
              <span className="mx-1.5 bg-gradient-to-r from-[#07F4F2] to-[#C1FAB8] bg-clip-text font-medium text-transparent">
                剧本
              </span>
              到
              <span className="mx-1.5 bg-gradient-to-r from-[#07F4F2] to-[#C1FAB8] bg-clip-text font-medium text-transparent">
                成片
              </span>
              ，一站完成创作
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-zinc-300">
            {['AI 剧本', '角色/场景/道具', '分镜图/视频', 'AI 配音', '时间线合成', '一键导出'].map(item => (
              <span
                key={item}
                className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 backdrop-blur-xl"
              >
                {item}
              </span>
            ))}
          </div>

          <div className="flex flex-col items-center justify-center gap-4 pt-6 sm:flex-row">
            <Link href={isAuthenticated ? '/projects' : '/auth/login'}>
              <Button size="lg" className={`${gradientBtn} h-14 px-8 text-lg`}>
                <Sparkles className="mr-2 h-5 w-5" />
                {isAuthenticated ? '进入项目工作台' : '立即开始创作'}
              </Button>
            </Link>

            <ChaseLightButton onClick={onDiscoverWorkflow} className="min-w-[188px]">
              <span className="flex h-14 items-center justify-center gap-2 px-8 text-lg">
                了解创作流程
                <ArrowRight className="h-5 w-5" />
              </span>
            </ChaseLightButton>
          </div>
        </motion.div>
      </div>

      <motion.button
        type="button"
        aria-label="滚动到创作流程"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        onClick={onDiscoverWorkflow}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 items-center justify-center rounded-full"
      >
        <span className="flex h-[52px] w-[32px] justify-center rounded-full border border-white/15 bg-white/[0.03] p-2 backdrop-blur-xl">
          <motion.span
            className="h-2 w-2 rounded-full bg-[#07F4F2] shadow-[0_0_14px_rgba(7,244,242,0.85)]"
            animate={{ y: [0, 12, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          />
        </span>
      </motion.button>
    </section>
  )
}
